import { useMemo } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  getTextHeightForParagraph,
  updateDataByType
} from '../utils/functions';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import commonStore from '../../../../store/common';
import { MULTIPLE_TABLE_NAMES } from '../consts/tableSections';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

export default function useMultpleTable({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  isMultiFunds,
  funds,
  onAddColumn,
  onDeleteColumn,
  tableName,
  columnLables,
  order = '',
  hasTableOrderNumber = true
}) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey = type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id');
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, tableName);
  };

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = value => {
    const changes = value[0];
    const fund = isMultiFunds
      ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data[tableName]))
      : JSON.parse(JSON.stringify(data));

    fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });
    onChangeFund(tableName, fund, changes.newCell?.fundId);
  };

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );
  const columns = useMemo(() => {
    const _fundSize = Object.keys(funds).length;
    const _columns = isMultiFunds
      ? _fundSize <= 3 && !isComparisonReport
        ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / (_fundSize + 1)
          }))
        : isComparisonReport
          ? [
              {
                columnId: 1,
                width: 200
              },
              ...Object.values(funds)
                .map(item => [item, item.previous_fund])
                .flat()
                .map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
            ]
          : [
              {
                columnId: 1,
                width: 200
              },
              ...Object.keys(funds).map((_, index) => ({
                columnId: index + 2,
                width: 300
              }))
            ]
      : isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 2
          }));

    return [
      {
        columnId: 0,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, data, contentWidth, isComparisonReport]);

  const rows = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      fundSize: Object.keys(funds).length,
      contentWidth,
      isComparisonReport
    });

    let _rows = [];
    if (isMultiFunds && !isComparisonReport) {
      _rows = [
        {
          rowId: 0,
          height: 40,
          cells: [
            {
              type: 'header',
              text: hasTableOrderNumber ? `${order}` : ''
            },
            {
              type: 'header',
              text: ''
            },
            ...Object.values(funds).map(item => ({
              type: 'header',
              text: item?.fund_name || '',
              style: {
                'justify-content': 'center',
                'white-space': 'inherit'
              }
            }))
          ]
        }
      ];
    }

    let cells = [];
    let count = 1;
    const filteredData = data.filter(item => !item.is_deleted);
    const isVisibleDelete = filteredData.length > 1;
    if (isMultiFunds) {
      let columnIndex = 0;
      data.forEach((item, ind) => {
        if (!item.is_deleted) {
          let index2 = columnIndex;
          Object.keys(columnLables).map((key, index) => {
            if (index === 0) {
              const title = `${MULTIPLE_TABLE_NAMES[tableName]} ${data.length > 1 ? index2 + 1 : ''}`;
              cells.push({
                rowId: count,
                height: getTextHeightForParagraph(title, labelWidth),
                cells: [
                  {
                    type: 'header',
                    text: ''
                  },

                  {
                    type: 'customHeader',
                    text: '',
                    render: () => (
                      <CustomHeaderCell
                        title={title}
                        onAddColumn={() => onAddColumn(tableName, ind)}
                        onDeleteColumn={() => onDeleteColumn(tableName, ind)}
                        hasDeleteBtn={isVisibleDelete}
                      />
                    )
                  },
                  ...(isComparisonReport
                    ? Object.values(funds)
                        .map(item => [item, item.previous_fund])
                        .flat()
                        .map((_, j) => ({
                          type: 'header',
                          text: item?.fund_name || '',
                          colspan: j % 2 === 0 ? 2 : undefined
                        }))
                    : Object.values(funds).map(() => ({
                        type: 'header',
                        text: '',
                        style: {
                          'justify-content': 'flex-start',
                          'white-space': 'inherit'
                        }
                      })))
                ]
              });
              count++;
            }

            if (isComparisonReport && index === 0) {
              cells.push({
                rowId: count,
                height: 40,
                cells: [
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  ...Object.values(funds)
                    .map(item => [item.previous_fund, item])
                    .flat()
                    .map(item => ({
                      type: 'header',
                      text: `${item?.period}, ${item?.year}`
                    }))
                ]
              });
              count++;
            }

            // Each value column stacks its own value above its own error, so
            // the row must fit the tallest column — pairing the longest value
            // with a different column's longest error over-sizes it.
            const rows = isComparisonReport
              ? Object.values(funds)
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => fund?.fund_data?.[tableName]?.[ind])
              : Object.values(funds).map(fund => fund?.fund_data?.[tableName]?.[ind]);

            const rowHeight = getRowHeight({
              label: columnLables[key]?.name,
              labelWidth,
              cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
              valueWidth
            });

            cells.push({
              rowId: count,
              height: rowHeight,
              cells: [
                {
                  type: 'header',
                  text: `${order + index + (hasTableOrderNumber ? 1 : 0)}`
                },

                {
                  type: 'customHeader',
                  text: '',
                  render: () => (
                    <CustomHeaderCell title={columnLables[key].name} hasAddBtn={false} />
                  )
                },
                ...(isComparisonReport
                  ? Object.values(funds)
                      .map(value => [value, value.previous_fund])
                      .flat()
                      .map((value, j) => {
                        const tableRows = value?.fund_data?.[tableName] || [];
                        const rowValue = tableRows[ind];
                        const filterByEmptyObj = tableRows.filter(v => !v.id);
                        return getColumnByType({
                          type: schema?.types?.[key]?.type,
                          options: schema?.types?.[key]?.options,
                          key,
                          defaultValue: schema?.defaultValues?.[key],
                          index: ind,
                          value: rowValue,
                          required:
                            (filterByEmptyObj.length === tableRows.length && index2 === 0) ||
                            rowValue?.id
                              ? funds[value?.fund_id]?.required_tables?.[
                                  tableName
                                ]?.field_names?.includes(key)
                              : false,
                          tableName,
                          isStaticTemplate,
                          fundId: value?.fund_id,
                          isSectionDisabled: isTableDisabledForFund(value?.fund_id),
                          fundName: value?.fund_name,
                          hasDrillDownFeature:
                            !!rowValue?.report_id &&
                            rowValue?.[key] !== null &&
                            schema?.types?.[key]?.has_drill_down_feature,
                          reportId: rowValue?.report_id,
                          tableId: rowValue?.id,
                          title: columnLables[key]?.name,
                          isMultiFunds,
                          isEsma: true,
                          fieldOrder: order + index,
                          difference:
                            j % 2 === 1
                              ? funds[value?.fund_id]?.difference?.[tableName]?.[ind]?.[key] || {}
                              : null,
                          nonEditable: isComparisonReport
                        });
                      })
                  : Object.values(funds).map(value => {
                      const filterByEmptyObj = value.fund_data[tableName].filter(v => !v.id);
                      return getColumnByType({
                        type: schema?.types?.[key]?.type,
                        options: schema?.types?.[key]?.options,
                        key,
                        defaultValue: schema?.defaultValues?.[key],
                        index: ind,
                        value: value.fund_data[tableName][ind],
                        required:
                          (filterByEmptyObj.length === value.fund_data[tableName].length &&
                            index2 === 0) ||
                          value?.fund_data?.[tableName]?.[ind]?.id
                            ? funds[value.fund_id]?.required_tables?.[
                                tableName
                              ]?.field_names?.includes(key)
                            : false,
                        tableName,
                        isStaticTemplate,
                        fundId: value.fund_id,
                        isSectionDisabled: isTableDisabledForFund(value.fund_id),
                        fundName: value.fund_name,
                        hasDrillDownFeature:
                          !!value.fund_data[tableName][ind]?.report_id &&
                          value.fund_data[tableName][ind]?.[key] !== null &&
                          schema?.types?.[key]?.has_drill_down_feature,
                        reportId: value.fund_data[tableName][ind]?.report_id,
                        tableId: value.fund_data[tableName][ind]?.id,
                        title: columnLables[key]?.name,
                        isMultiFunds,
                        isEsma: true,
                        fieldOrder: order + index,
                        nonEditable: isComparisonReport
                      });
                    }))
              ]
            });

            count++;
          });
          columnIndex++;
        }
      });
    } else {
      let columnIndex = 0;
      data.forEach((item, ind) => {
        if (!item.is_deleted) {
          let index2 = columnIndex;
          Object.keys(columnLables).forEach((key, index) => {
            const currentFund = funds[currentDataKey];
            const previousFund = currentFund?.previous_fund;
            const previousRow = previousFund?.fund_data?.[tableName]?.[ind];

            if (index === 0) {
              const title = `${MULTIPLE_TABLE_NAMES[tableName]} ${data.length > 1 ? index2 + 1 : ''}`;
              cells.push({
                rowId: count,
                height: getTextHeightForParagraph(title, labelWidth),
                cells: [
                  {
                    type: 'header',
                    text: hasTableOrderNumber ? `${order}` : ''
                  },

                  {
                    type: 'customHeader',
                    text: '',
                    render: () => (
                      <CustomHeaderCell
                        title={title}
                        onAddColumn={() => onAddColumn(tableName, ind)}
                        onDeleteColumn={() => onDeleteColumn(tableName, ind)}
                        hasDeleteBtn={isVisibleDelete}
                      />
                    )
                  },
                  ...(isComparisonReport
                    ? [
                        {
                          type: 'header',
                          text: `${previousFund?.period}, ${previousFund?.year}`
                        },
                        {
                          type: 'header',
                          text: `${currentFund?.period}, ${currentFund?.year}`
                        }
                      ]
                    : [{ type: 'header', text: '' }])
                ]
              });
              count++;
            }

            const rows = isComparisonReport ? [previousRow, data?.[ind]] : [data?.[ind]];

            const rowHeight = getRowHeight({
              label: columnLables[key]?.name,
              labelWidth,
              cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
              valueWidth
            });

            const filterByEmptyObj = data.filter(v => !v.id);

            cells.push({
              rowId: count,
              height: rowHeight,
              cells: [
                {
                  type: 'header',
                  text: `${order + index + (hasTableOrderNumber ? 1 : 0)}`
                },

                {
                  type: 'customHeader',
                  text: '',
                  render: () => (
                    <CustomHeaderCell
                      title={columnLables[key].name}
                      hasDrillDown={
                        data[ind]?.report_id &&
                        schema?.types?.[key]?.has_drill_down_feature &&
                        data[ind]?.[key] !== null
                      }
                      hasAddBtn={false}
                      reportId={data[ind]?.report_id}
                      fundId={data[ind]?.fund_id}
                      columnName={key}
                      tableName={tableName}
                      tableId={data[ind]?.id}
                    />
                  ),
                  style: {
                    'justify-content': 'flex-start',
                    'white-space': 'inherit'
                  }
                },
                ...(isComparisonReport
                  ? [
                      getColumnByType({
                        type: schema?.types?.[key]?.type,
                        options: schema?.types?.[key]?.options,
                        key,
                        defaultValue: schema?.defaultValues?.[key],
                        index: ind,
                        value: previousRow,
                        required:
                          (filterByEmptyObj.length === data.length && index2 === 0) || item?.id
                            ? requiredFields.includes(key)
                            : false,
                        tableName,
                        isStaticTemplate,
                        isMultiFunds,
                        isEsma: true,
                        fundId: previousFund?.fund_id,
                        isSectionDisabled: isTableDisabledForFund(previousFund),
                        fundName: previousFund?.fund_name,
                        fieldOrder: order + index,
                        nonEditable: isComparisonReport
                      })
                    ]
                  : []),
                getColumnByType({
                  type: schema?.types?.[key]?.type,
                  options: schema?.types?.[key]?.options,
                  key,
                  defaultValue: schema?.defaultValues?.[key],
                  index: ind,
                  value: item,
                  required:
                    (filterByEmptyObj.length === data.length && index2 === 0) || item?.id
                      ? requiredFields.includes(key)
                      : false,
                  tableName,
                  isStaticTemplate,
                  //height: getTextHeightForParagraph(String(item[key]), '150px'),
                  isMultiFunds,
                  isEsma: true,
                  fundId: item?.fund_id,
                  isSectionDisabled: isTableDisabledForFund(item?.fund_id),
                  fundName: item?.fund_name,
                  fieldOrder: order + index,
                  difference: isComparisonReport
                    ? funds[currentDataKey]?.difference?.[tableName]?.[ind]?.[key] || {}
                    : null,
                  nonEditable: isComparisonReport
                })
              ]
            });

            count++;
          });
          columnIndex++;
        }
      });
    }

    return [..._rows, ...cells];
  }, [data, isMultiFunds, isStaticTemplate, isComparisonReport]);

  return { rows, columns, onChange };
}
